import React, { useEffect, useState } from "react"
import { KSpan, KTitleSpan, KDropdown, KSelectRange, KButton, KTooltip } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { lang } from "../../../../constants/languages"
import upcomingColumns from "./upcomingColumns"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { summaries } from "../../../../apis"
import {
  getCurrency,
  getMonthRange,
  mapListToCamelCase,
  mapToCamelCase,
  yyyymmToDate,
  getLastNMonthsYYYYMM
} from "../../../../utility"
import { SummaryUpcoming, SummaryTotals, UpcomingTotals } from "../../../../types"
import Loading from "../../../ui/Loading"

const Upcoming = ({
  isAdmin,
  platforms,
  selectedUser,
  selectedAccount
}: {
  isAdmin: boolean
  platforms: KSelectOption[]
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}) => {
  const [loading, setLoading] = useState(false)

  const [offset, setOffset] = useState(0)
  const [summaryList, setSummaryList] = useState<SummaryUpcoming[]>([])
  const [upcomingTotals, setUpcomingTotals] = useState<UpcomingTotals[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [selectedPeriodList, setSelectedPeriodList] = useState<string[]>([])

  const getRoyaltiesUpcoming = (
    selectedPeriodList: string[],
    selectedPlatforms: KSelectOption[],
    upcomingTotals?: UpcomingTotals[]
  ) => {
    setLoading(true)
    const isAdminParam = isAdmin ? "&is_admin_page=1" : "&is_admin_page=0"
    const dspParam =
      selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.map((option) => option.value).join(",")}` : ""
    const periodParam =
      selectedPeriodList.length > 0
        ? `&period_start=${selectedPeriodList[0]}&period_end=${selectedPeriodList[selectedPeriodList.length - 1]}`
        : ""
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const selectedAccountParam = `&selected_account=${selectedAccount?.value2}`

    summaries(
      `/royalties_upcoming/?limit=${limit}&offset=${offset}${isAdminParam}${dspParam}${periodParam}${userIdParam}${selectedAccountParam}`,
      "get"
    )
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          return
        }

        if (!upcomingTotals && resp.data.totals) {
          const upcomingTotals: UpcomingTotals[] = mapListToCamelCase(resp.data.totals)
          setUpcomingTotals(upcomingTotals)
        }

        const summaryList: SummaryUpcoming[] = mapListToCamelCase(resp.data.results)
        setSummaryList(summaryList)

        if (selectedPeriodList.length === 0 && summaryList.length > 0) {
          setSelectedPeriodList(getLastNMonthsYYYYMM(6))
        }
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (isAdmin && !selectedUser?.value) {
      return
    }
    setUpcomingTotals([])
    setSummaryList([])
    setSelectedPlatforms([])
    setSelectedPeriodList([])
    getRoyaltiesUpcoming([], [], undefined)
  }, [selectedUser, selectedAccount])

  const limit = 10

  const topRightElements = () => {
    return (
      <div className="flex gap-4">
        <div className="flex gap-6">
          {selectedPeriodList.length > 0 && (
            <div className="flex">
              <KSelectRange
                key={`filter-date-range`}
                value={[
                  yyyymmToDate(selectedPeriodList[0]),
                  yyyymmToDate(selectedPeriodList[selectedPeriodList.length - 1])
                ]}
                onChange={(event) => {
                  if (!event) {
                    setSelectedPeriodList([])
                    return
                  }
                  //@ts-ignore
                  const monthRage = getMonthRange(event[0], event[1])
                  if (monthRage.join(" ") === selectedPeriodList.join(" ")) {
                    return
                  }
                  setSelectedPeriodList(monthRage)
                  getRoyaltiesUpcoming(monthRage, selectedPlatforms, upcomingTotals)
                }}
                height="36px"
                width="auto"
                backgroundColor="#F7F7F7"
                hoverBackgroundColor="#F3F3F3"
                boxShadow="none"
              />
              <div className="flex ml-1 mr-2 items-center gap-1">
                <KSpan text={`${selectedPeriodList[0]}`} />
                {selectedPeriodList.length > 1 && (
                  <KSpan text={`- ${selectedPeriodList[selectedPeriodList.length - 1]}`} />
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-start max-w-[260px]">
            <KDropdown
              isClearable
              options={platforms}
              isMulti
              onSelect={(elements) => {
                setSelectedPlatforms(elements as KSelectOption[])
              }}
              placeholder={lang.analytics.all_platforms_placeholder}
              showOnlyIconsInMulti
              allowContainerShrink
              selected={selectedPlatforms}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={170}
              enableIndicator={true}
              placeholderColor="#000"
              closeMenuOnSelect={false}
              onBlur={() => getRoyaltiesUpcoming(selectedPeriodList, selectedPlatforms, upcomingTotals)}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderWidget = () => {
    return (
      <div className="flex gap-4 pr-8">
        {upcomingTotals.map((upcomingTotal) => {
          const revenue = upcomingTotal?.estimatedRevenue || 0
          const estimatedRevenue = upcomingTotal?.estimatedRevenue?.toLocaleString("en-EN", {
            maximumFractionDigits: 2
          })
          const estimatedRevenueStart = (revenue * 0.77)?.toLocaleString("en-EN", { maximumFractionDigits: 2 })
          const estimatedRevenueEnd = (revenue * 1.22)?.toLocaleString("en-EN", { maximumFractionDigits: 2 })

          const currency = getCurrency(upcomingTotal?.currency || "")
          return (
            <div
              className="p-4"
              style={{ border: "1px solid #11111120", borderRadius: 5 }}
              key={`upcoming_total_${upcomingTotal.paymentDate}`}
            >
              <div className="flex justify-between items-center" style={{ width: 240 }}>
                <KSpan text={`${lang.royalties.upcoming.widget.estimated_revenue}`} />
                <KSpan text={`(${upcomingTotal.paymentDate})`} fontWeight={500} />
              </div>
              <div className="mt-2">
                <KSpan
                  fontSize={28}
                  lineHeight="32px"
                  fontWeight={500}
                  color="#111"
                  text={`${currency}${estimatedRevenue} `}
                />
              </div>
              <div className="mt-2 flex gap-1">
                <KSpan text={`${currency}${estimatedRevenueStart} `} />
                <KSpan text="-" />
                <KSpan text={`${currency}${estimatedRevenueEnd} `} />
              </div>
              <div className="mt-3">
                {/* Progress bar */}
                <div className="w-full h-3 rounded bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${upcomingTotal?.progress || 0}%` }}
                  />
                </div>
              </div>
              <div className="mt-1">
                <KSpan
                  text={`${upcomingTotal?.progress?.toFixed(1) || 0}% ${lang.royalties.upcoming.widget.complete}`}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex-grow justify-center">
      {loading && <Loading />}
      <div className="w-[100%] flex pt-6 pl-3">{renderWidget()}</div>
      <div className="w-[100%] flex ">
        <div className="grow min-h-0">
          <TableNew
            className="pt-2.5 -ml-5"
            title={<KTitleSpan fontSize={20} lineHeight="26px" text={lang.royalties.upcoming.title} />}
            minRowCount={limit}
            limit={limit}
            columns={upcomingColumns}
            data={summaryList}
            count={1}
            loading={loading}
            topRightElement={topRightElements()}
            overrideTableOverflow="auto"
            notFoundTitle={lang.not_found.reports.title}
            notFoundDesc={lang.not_found.reports.desc}
            tablePaddingTop={6}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Upcoming)
